import React, { useEffect, useRef, useState } from 'react';
import { Loader2, X } from 'lucide-react';
import useSWR from 'swr';

import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from 'components/ui/popover';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandItem,
  CommandList,
} from 'components/ui/command';
import { Button } from 'components/ui/button';
import { useDebounce } from '../../hooks/use-debounce';

const MAPBOX_TOKEN = process.env.REACT_APP_MAPBOX_ACCESS_TOKEN || '';

// Cache features so we can extract city + region without a second call
const featureCache = new Map<string, any>();

function getMapboxClient() {
  const w = window as any;
  if (!w.mapboxSdk || !w.mapboxgl?.accessToken) {
    throw new Error('Mapbox SDK not loaded');
  }
  return w.mapboxSdk({ accessToken: w.mapboxgl.accessToken });
}

interface CityResult {
  id: string;
  label: string;
}

async function fetchCities(input: string): Promise<CityResult[]> {
  if (!MAPBOX_TOKEN) return [];
  const client = getMapboxClient();
  const response = await client.geocoding
    .forwardGeocode({
      query: input,
      types: ['place'],
      limit: 5,
      language: ['en'],
      countries: ['us', 'ca'],
      proximity: 'ip',
    })
    .send();

  const features = response.body.features || [];
  return features.map((f: any) => {
    featureCache.set(f.id, f);
    return {
      id: f.id,
      label: f.place_name || f.text,
    };
  });
}

interface CityData {
  name: string;
  postalCode: string;
}

function extractCityData(featureId: string): CityData {
  const feature = featureCache.get(featureId);
  if (!feature) return { name: '', postalCode: '' };

  const city = feature.text || '';
  const context: any[] = feature.context || [];
  const region = context.find((c: any) => c.id.startsWith('region'));
  const postcode = context.find((c: any) => c.id.startsWith('postcode'));
  const regionCode = region?.short_code?.replace(/^US-|^CA-/, '') || region?.text || '';

  return {
    name: regionCode ? `${city}, ${regionCode}` : city,
    postalCode: postcode?.text || '',
  };
}

// ─── Component ───────────────────────────────────────────────────────

interface CityAutoCompleteProps {
  value: string;
  onChange: (value: string) => void;
  onBlur?: () => void;
  placeholder?: string;
  /** Called with the postal code when a city is selected (for auto-filling zip). */
  onPostalCode?: (zip: string) => void;
}

export function CityAutoComplete({
  value,
  onChange,
  onBlur,
  placeholder = 'Search for your city...',
  onPostalCode,
}: CityAutoCompleteProps) {
  const [searchInput, setSearchInput] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [selected, setSelected] = useState(!!value);
  const inputRef = useRef<HTMLInputElement>(null);

  const debouncedInput = useDebounce(searchInput, 500);

  const { data, isLoading } = useSWR(
    debouncedInput ? ['city-autocomplete', debouncedInput] : null,
    () => fetchCities(debouncedInput),
    { revalidateOnFocus: false },
  );

  const results: CityResult[] = data || [];

  useEffect(() => {
    setIsOpen(searchInput.length > 0 && !selected);
  }, [searchInput, selected]);

  // If value is set externally (e.g. going back in wizard), show it
  useEffect(() => {
    if (value && !searchInput) {
      setSelected(true);
    }
  }, [value]);

  const handleSelect = (result: CityResult) => {
    const data = extractCityData(result.id);
    onChange(data.name);
    if (data.postalCode && onPostalCode) {
      onPostalCode(data.postalCode);
    }
    setSearchInput('');
    setSelected(true);
    setIsOpen(false);
  };

  const handleClear = () => {
    onChange('');
    setSearchInput('');
    setSelected(false);
    inputRef.current?.focus();
  };

  if (selected && value) {
    return (
      <div className="flex items-center gap-2">
        <div className="flex h-10 w-full items-center rounded-md border bg-background px-3 text-sm">
          {value}
        </div>
        <Button
          type="button"
          onClick={handleClear}
          size="icon"
          variant="outline"
          className="shrink-0"
        >
          <X className="size-4" />
        </Button>
      </div>
    );
  }

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild>
        <div className="flex w-full items-center justify-between rounded-lg border bg-background ring-offset-background text-sm focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-0">
          <input
            ref={inputRef}
            value={searchInput}
            onChange={e => {
              setSearchInput(e.target.value);
              setSelected(false);
            }}
            onFocus={() => searchInput.length > 0 && setIsOpen(true)}
            onBlur={onBlur}
            placeholder={placeholder}
            className="w-full h-10 px-3 py-2 rounded-lg outline-none text-sm bg-transparent"
          />
        </div>
      </PopoverTrigger>
      <PopoverContent
        className="p-0"
        align="start"
        sideOffset={4}
        onOpenAutoFocus={e => e.preventDefault()}
        style={{ minWidth: 'var(--radix-popover-trigger-width)' }}
      >
        <Command shouldFilter={false}>
          <CommandList className="max-h-[240px] text-sm">
            <CommandGroup>
              {isLoading ? (
                <div className="h-28 flex items-center justify-center">
                  <Loader2 className="size-6 animate-spin" />
                </div>
              ) : (
                results.map(result => (
                  <CommandItem
                    key={result.id}
                    value={result.label}
                    onSelect={() => handleSelect(result)}
                    className="cursor-pointer text-sm"
                  >
                    {result.label}
                  </CommandItem>
                ))
              )}
            </CommandGroup>
            {!isLoading && results.length === 0 && debouncedInput && (
              <CommandEmpty>
                <div className="py-4 flex items-center justify-center text-sm">
                  No cities found
                </div>
              </CommandEmpty>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
